import { Theme, ThemeOptions, createTheme } from '@mui/material/styles';

/**
 * Design system « Birdz Web Console », transcrit depuis la charte graphique
 * (Figma « Web console », page *Charte graphique — Design style*).
 *
 * Les couleurs reprennent volontairement les valeurs déjà présentes dans
 * `src/_variables.scss` : la charte et ce fichier ont la même origine, il ne faut
 * pas les faire diverger.
 */
export const BIRDZ_COLORS = {
  /** Navy : barre d'application, titres, chrome des tables, boutons primaires. */
  primary: '#3A4255',
  secondary: '#7ED17C',
  /**
   * Accent de signature de la charte (H2, titres de modale).
   *
   * Non appliqué dans l'application : le thème B-Tech laisse l'utilisateur choisir
   * son accent parmi six (`themeColors` de la BRL), et c'est ce comportement qui
   * prime. Exporté pour les cas qui exigeraient explicitement l'orange de la charte.
   */
  tertiary: '#F7A677',
  info: '#95CDE8',
  error: '#D32F2F',
  /** Aplats de fond. `rainDrop` est le canevas de l'application dans la charte. */
  softTints: {
    rainDrop: '#E7EEE8',
    frenchManicure: '#FFE6DC',
    hintOfMint: '#E0F4D3'
  },
  /** Palette de catégorisation, pour distinguer des séries ou des statuts. */
  extended: {
    aqua: '#65D7CE',
    eucalyptus: '#98CAC4',
    purple: '#94509F',
    lavender: '#7583C3',
    gold: '#AF9950'
  },
  link: '#5C6BC0',
  border: 'rgba(0, 0, 0, 0.12)'
} as const;

/**
 * Un seul rayon standard, appliqué uniformément aux boutons, champs, cartes et
 * panneaux. Les chips sont la seule exception : pilules pleines.
 */
export const BIRDZ_RADIUS = {
  standard: 20,
  pill: 9999
} as const;

export const BIRDZ_SHADOWS = {
  /** Cartes, modales et barres d'outils de table. */
  cardHalo: '0 0 20px rgba(0, 0, 0, 0.12)',
  popover: '0 4px 14px rgba(0, 0, 0, 0.18)'
} as const;

/** Transitions courtes au survol et au clic, sans rebond. */
const TRANSITION = 'all .15s ease';

/**
 * Chrome de table de la charte : en-têtes navy, libellés blancs. Surcharges de composants
 * à étendre dans le thème d'un écran (`components: { ...navyTableHeadComponents(base) }`),
 * pensées pour le `BTable` de la BRL.
 */
export function navyTableHeadComponents(theme: Theme): NonNullable<ThemeOptions['components']> {
  const headerText = theme.palette.common.white;

  return {
    MuiTableHead: {
      styleOverrides: {
        // La cellule « Actions » de BTable (collante à droite) pose en sx un fond
        // blanc et un texte gris : un sx l'emporte sur les styleOverrides de la
        // cellule elle-même, seul un sélecteur descendant (.thead .cellule) le bat.
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: BIRDZ_COLORS.primary,
            color: headerText
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        // `head` est fusionné dans la même règle que le style de base de la cellule
        // (y compris stickyHeader, utilisé par BTable) : il l'emporte sans dépendre
        // de la spécificité.
        head: {
          backgroundColor: BIRDZ_COLORS.primary,
          color: headerText,
          fontWeight: 600,
          // HeaderCell de la BRL pose `color: #666666` en sx sur le libellé et
          // l'icône de tri, sur un élément enfant : seul un sélecteur plus
          // spécifique (.cellule.header-cell *) peut le surcharger.
          '&.header-cell *': { color: headerText }
        }
      }
    }
  };
}

/**
 * Étend un thème MUI avec les tokens de la charte, sans toucher aux polices.
 *
 * Volontairement absent :
 * - `spacing` — la charte est sur une base 4px, MUI sur 8px, et toutes les valeurs
 *   de la charte s'expriment en demi-pas. Redéfinir la base doublerait tous les
 *   espacements déjà posés dans les écrans.
 * - `typography` — aucune police n'est chargée dans le projet (ni Montserrat ni
 *   Roboto), et `App.scss` impose `body { font-size: 15px }`, ce qui désynchronise
 *   les `rem`. À reprendre avec les polices.
 * - le fond « Rain Drop » — `App.scss` pose une image de fond sur `body` ; changer
 *   le canevas est une décision applicative, pas un token de composant.
 */
export function withBirdzDesignSystem(parentTheme: Partial<Theme>): Theme {
  // MUI passe un objet vide au callback quand aucun ThemeProvider n'est monté
  // au-dessus (cf. EMPTY_THEME dans @mui/system) : `parentTheme.palette` peut donc
  // être absent malgré le type `Theme` annoncé. On complète avec les défauts.
  const base = createTheme(parentTheme);
  const augment = (main: string) => base.palette.augmentColor({ color: { main } });

  return createTheme(base, {
    palette: {
      // `augmentColor` calcule `light`, `dark` et `contrastText`. Indispensable ici :
      // `src/styles/theme.ts` déclare `light: ''` et `dark: ''`, et une fusion de
      // thèmes conserverait ces chaînes vides au lieu de les recalculer.
      primary: augment(BIRDZ_COLORS.primary),
      secondary: augment(BIRDZ_COLORS.secondary),
      info: augment(BIRDZ_COLORS.info),
      error: augment(BIRDZ_COLORS.error)
    },
    shape: {
      borderRadius: BIRDZ_RADIUS.standard
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: BIRDZ_RADIUS.standard
          },
          // Les cartes de la charte portent un halo diffus plutôt qu'une bordure.
          // `PagePaper` de la BRL force `boxShadow: 'none'` en sx et reste donc plat.
          elevation1: {
            boxShadow: BIRDZ_SHADOWS.cardHalo
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: BIRDZ_RADIUS.standard,
            boxShadow: BIRDZ_SHADOWS.cardHalo
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: BIRDZ_RADIUS.standard,
            transition: TRANSITION
          },
          // Comportement Material sans ondulation : assombrir au survol,
          // enfoncer au clic.
          contained: {
            '&:hover': {
              filter: 'brightness(0.93)'
            },
            '&:active': {
              transform: 'translateY(1px)'
            }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            // Le fieldset interne hérite de ce rayon.
            borderRadius: BIRDZ_RADIUS.standard
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: BIRDZ_SHADOWS.popover
          }
        }
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            boxShadow: BIRDZ_SHADOWS.popover
          }
        }
      }
    }
  });
}
